import axios from "@/libs/axios";
import type { ApiKey } from "@/types/apiKey.types";
import { RequestQueryBuilder } from "@rewiko/crud-request";
import { useQuery } from "@tanstack/vue-query";
import { AxiosResponse } from "axios";
import { defineStore } from "pinia";
import { PaginatedResponseInterface } from "@/interfaces/paginatedResponse.interface";

interface State {
    isLoadingApiKeys: boolean;
    isLoadingApiKey: boolean;
    apiKeys: ApiKey[];
}

export const useApiKeysStore = defineStore("api-keys", {
    state: (): State => ({
        isLoadingApiKeys: false,
        isLoadingApiKey: false,
        apiKeys: [],
    }),
    actions: {
        // @TODO: Handle multiple pages
        fetchApiKeys(requestQueryBuilder?: RequestQueryBuilder): void {
            useQuery<Pick<AxiosResponse<PaginatedResponseInterface<ApiKey>>, "data">>(
                ["api-keys", requestQueryBuilder?.queryObject],
                () => {
                    this.isLoadingApiKeys = true;
                    return axios.get<PaginatedResponseInterface<ApiKey>>(`${process.env.VUE_APP_PUBLIC_API}/api-keys`, {
                        params: requestQueryBuilder?.queryObject,
                    });
                },
                {
                    //staleTime: 300000, // 5min
                    refetchOnWindowFocus: false,
                    placeholderData: {
                        data: {
                            data: [],
                            count: 0,
                            page: 0,
                            pageCount: 0,
                            total: 0,
                        },
                    } as AxiosResponse<PaginatedResponseInterface<ApiKey>>,
                    onSuccess: (response) => (this.apiKeys = response.data.data),
                    onSettled: () => (this.isLoadingApiKeys = false),
                }
            );
        },
        createApiKey(apiKey: ApiKey): Promise<ApiKey> {
            this.isLoadingApiKey = true;

            return axios
                .post<ApiKey>(`${process.env.VUE_APP_PUBLIC_API}/api-keys`, apiKey)
                .then(({ data }) => {
                    this.apiKeys.unshift(data);

                    return data;
                })
                .finally(() => (this.isLoadingApiKey = false));
        },
        deleteApiKey(apiKeyId: number): Promise<number> {
            this.isLoadingApiKey = true;

            return axios
                .delete(`${process.env.VUE_APP_PUBLIC_API}/api-keys/${apiKeyId}`)
                .then(({ data }) => {
                    const index = this.apiKeys.findIndex((key: ApiKey) => key.id === apiKeyId);
                    this.apiKeys.splice(index, 1);

                    return data.affected;
                })
                .finally(() => (this.isLoadingApiKey = false));
        },
    },
    getters: {
        getApiKeyById(): (apiKeyId: number) => ApiKey | undefined {
            return (apiKeyId: number) => this.apiKeys.find((key: ApiKey) => key.id === apiKeyId);
        },
    },
});
